import { isCurrencyConversionCategory, validateConversionChoice } from "../../lib/currency-conversions";
import LoanTransactionFields from "./LoanTransactionFields";
import EntryAllocationFields from "./EntryAllocationFields";
import { useReceiptInput } from "./use-receipt-input";
import type { ReceiptDraft } from "../../types/receipt";
import { checkedAllocations } from "../../lib/allocations";
import { checkedTrackedItems } from "../../lib/tracked-items";
import { useEntrySave } from "./use-entry-save";
import { useMarkupCalc } from "./use-markup-calc";
import ExpenseSaved from "./ExpenseSaved";
import LoadingLabel from "../../components/LoadingLabel";
import DateInput from "../../components/DateInput";
import { useProfileNames } from "../../components/ProfilesContext";
import { useLoanEntry } from "./use-loan-entry";
import { useRecurringEntry } from "./use-recurring-entry";
import { lazy, Suspense, useState, useEffect, useRef, useMemo } from "react";
import type { FormEvent, ReactNode } from "react";
import { tripTitleList } from "../../lib/coverage";
import { getDefaultTripToAccount, getTripAccounts } from "../../lib/trips";
import {
  ArrowRight,
  ChevronDown,
  LockKeyhole,
  ReceiptText,
} from "lucide-react";
import type {
  DashboardData,
  ExpenseInput,
  Owner,
  TrackedItemInput,
} from "../../types";
import ConfirmDialog, { type ConfirmState } from "../../components/ConfirmDialog";
import { reconciledMonths, reconciliationMonthLabel } from "../../lib/reconciliation";
import EntryTitlePayeeFields from "./EntryTitlePayeeFields";
import EntryCategoryFields, { categoryLists } from "./EntryCategoryFields";
import ExpenseOptionalDetails from "./ExpenseOptionalDetails";
import EntryAmountField from "./EntryAmountField";
import EntryAccountField from "./EntryAccountField";
import EntryTripFields from "./EntryTripFields";
import { createInitialExpenseInput } from "./initial-expense-input";

// Transfer-only FX code and styles are precached, then loaded when the type is selected.
const TransferFields = lazy(() => import("./TransferFields"));
const EntryCurrencyConversionFields = lazy(() => import("./EntryCurrencyConversionFields"));

export interface ExpenseFormProps {
  allowConversion?: boolean;
  editingTransactionId?: string;
  data: DashboardData;
  profile: Owner;
  onSaved: (accountIds?: string[]) => void;
  onReview?: (input: ExpenseInput) => void;
  reviewLabel?: ReactNode;
  autoFocusAmount?: boolean;
  allowTracking?: boolean;
  externalBusy?: boolean;
  freezeFields?: boolean;
  initialInput?: ExpenseInput;
  onPendingChange?: (pending: boolean) => void;
  onDraftChange?: (input: ExpenseInput) => void;
  onAddAnother?: () => void;
  clearing?: boolean;
  loanTransaction?: boolean;
  receipt?: ReceiptDraft;
  onReceiptChange?: (receipt: ReceiptDraft) => void;
}

export default function ExpenseForm({
  data, editingTransactionId, allowConversion = true,
  profile,
  onSaved,
  initialInput,
  onPendingChange,
  onDraftChange,
  onReview,
  reviewLabel = "Done editing",
  autoFocusAmount = true,
  allowTracking = true,
  externalBusy = false,
  freezeFields = false,
  onAddAnother,
  clearing = false,
  loanTransaction = false,
  receipt, onReceiptChange,
}: ExpenseFormProps) {
  const profileNames = useProfileNames();
  const accounts = useMemo(() => data.accounts || [], [data.accounts]);
  const preferred =
    accounts.find(
      (a) =>
        a.active !== false && a.owner === profile && /primary/i.test(a.name),
    ) ||
    accounts.find((a) => a.active !== false && a.owner === profile) ||
    accounts[0];
  const trips = useMemo(() => data.trips || [], [data.trips]);

  const [input, setInput] = useState<ExpenseInput>(() =>
    createInitialExpenseInput({
      data,
      accounts,
      trips,
      preferred,
      initialInput,
      onReview,
    }),
  );

  const tripTitles = useMemo(() => (input.tripId ? tripTitleList(data, input.tripId) : []), [input.tripId, data]);

  const curTrip = useMemo(() => trips.find((t) => t.id === input.tripId), [trips, input.tripId]);
  const tripAccounts = useMemo(() => {
    if (!curTrip) return [];
    return getTripAccounts(curTrip, accounts, trips);
  }, [curTrip, accounts, trips]);
  const [trackedItems, setTrackedItems] = useState<TrackedItemInput[]>(
    () => initialInput?.trackedItems || [],
  );
  const { receiptInput, receiptError, changePlan } = useReceiptInput(input, setInput, setTrackedItems, receipt, onReceiptChange);
  const [more, setMore] = useState(
    clearing ||
      !!input.subcategory ||
      !!input.pending ||
      !!(input.split && input.split.type !== "joint"),
  );
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const { trackMarkup, setTrackMarkup, markupAutoCalc, setMarkupAutoCalc, markupBaseline, changeAmount, changeMarkup } =
    useMarkupCalc(input, setInput);
  // Only offered on a brand-new manual entry — editing an existing (possibly already
  // IOU-tagged) transaction goes through the plain expense path to avoid desyncing the IOU.
  const canTrackIOU = !onReview && !initialInput;
  const draftCallback = useRef(onDraftChange);
  draftCallback.current = onDraftChange;
  useEffect(() => {
    draftCallback.current?.({ ...receiptInput, trackedItems });
  }, [receiptInput, trackedItems]);
  const account = accounts.find((a) => a.id === input.accountId);
  const entryType = input.type || "Expense";
  const destination = accounts.find(a => a.id === input.toAccountId);
  const [localEntryOptions, setLocalEntryOptions] = useState<
    DashboardData["entryOptions"] | null
  >(null);
  const entryOptions = localEntryOptions || data.entryOptions || {
    categories: [], subcategories: [], subcategoriesByCategory: {},
    incomeCategories: [], incomeSubcategories: [], incomeSubcategoriesByCategory: {},
  };
  const isIncome = entryType === "Income";
  const loan = useLoanEntry({ data, input, account, isIncome, profile });
  const recurring = useRecurringEntry(canTrackIOU && !input.conversionTransactionId);
  const { locked, busy, error, setError, saved, savedOnDevice, saveEntry, saveLoanEntry } = useEntrySave({
    profile, input: receiptInput, trackedItems, account, loan, recurring, onSaved,
  });
  useEffect(() => { if (!receiptError) setError(""); }, [receiptError, setError]);
  useEffect(() => {
    onPendingChange?.(locked || busy);
  }, [locked, busy, onPendingChange]);
  const { isIOU } = loan;
  const { iouMode } = loan.fields;
  const { categories } = categoryLists(entryOptions, isIncome);
  const [categoryError, setCategoryError] = useState("");
  /** Switching kind starts the kind-specific parts over: its category, tracking, loan and transfer. */
  function changeEntryType(type: "Expense" | "Income" | "Transfer") {
    setTrackedItems([]);
    setTrackMarkup(false);
    loan.resetFor(type);
    const nextToAccId = type === "Transfer" && curTrip
      ? getDefaultTripToAccount(curTrip, accounts, input.accountId, input.tripDestination, trips)?.id || ""
      : "";
    setInput((v) => ({
      ...v,
      type,
      conversionTransactionId: undefined, conversionExpectedUpdatedAt: undefined,
      goalId: undefined,
      completeGoal: false,
      allocations: undefined,
      category: "Uncategorized",
      title: type === "Transfer" ? "Account transfer" : "",
      payeeId: type === "Transfer" ? undefined : v.payeeId,
      subcategory: "",
      reportableAmount: undefined,
      toAccountId: nextToAccId,
      exchangeRate: "",
      markup: "",
    }));
  }
  function field(key: keyof ExpenseInput, value: string) {
    setInput((v) => ({
      ...v,
      [key]: value,
      ...(key === "category" ? { subcategory: "", newSubcategory: undefined } : {}),
    }));
  }
  const optionalCount = [
    entryType === "Expense" && !isIOU && !loanTransaction && !clearing && !!input.pending,
    entryType === "Expense" && !isIOU && !loanTransaction && !clearing && !!(input.split && input.split.type !== "joint"),
    entryType !== "Transfer" && !isIOU && !input.allocations && input.reportableAmount !== undefined,
    entryType === "Expense" && !isIOU && trackMarkup,
    entryType !== "Transfer" && !input.allocations && canTrackIOU && isIOU,
    entryType !== "Transfer" && !isIOU && !input.allocations && recurring.fields.canMakeRecurring && recurring.fields.makeRecurring,
    entryType === "Expense" && !!input.goalId,
    allowTracking && entryType === "Expense" && !isIOU && trackedItems.length > 0,
  ].filter(Boolean).length;

  const optionalProps = {
    input, setInput, account, entryType, goals: data.goals, busy, externalBusy, freezeFields, locked,
    isIOU: isIOU || loanTransaction, loanTransaction, clearing, canTrackIOU, allowTracking,
    profileNames, payerProfile: account?.owner || profile,
    field, changeMarkup, trackMarkup, setTrackMarkup, markupAutoCalc, setMarkupAutoCalc,
    markupBaseline, trackedItems, setTrackedItems,
    loan: { isIncome, ...loan.fields },
    recurring: recurring.fields,
  };
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (externalBusy) return;
    if (receiptError) { setError(receiptError); return; }
    const conversionError = validateConversionChoice(receiptInput);
    if (conversionError) { setError(conversionError); return; }
    try { checkedAllocations(receiptInput); } catch (e) { setError((e as Error).message); return; }
    if (onReview) {
      try {
        onReview({ ...receiptInput, reportableAmount: receiptInput.reportableAmount ?? receiptInput.amount, trackedItems: checkedTrackedItems(trackedItems) });
      } catch (e) {
        setError((e as Error).message);
      }
      return;
    }
    if (isIOU) {
      await saveLoanEntry();
      return;
    }
    const months = reconciledMonths(data, [input.date]);
    if (months.length) {
      setConfirm({ title: "Reopen a reconciled month?", message: `This entry affects ${months.map(reconciliationMonthLabel).join(", ")}. Saving it will reopen the affected balance reviews.`, confirmLabel: "Save anyway", onConfirm: saveEntry });
      return;
    }
    await saveEntry();
  }

  if (saved)
    return (
      <ExpenseSaved
        heading={iouMode === "new" ? "Loan saved." : iouMode === "settle" ? "Repayment saved." : `${entryType} saved${input.completeGoal ? " · Goal achieved" : ""}.`}
        amount={Number(input.amount)}
        currency={account?.currency}
        detail={isIOU ? (data.payees || []).find((p) => p.id === input.payeeId)?.name || input.payeeName || "" : `${input.title} · ${input.category}${input.allocations && input.allocations.length > 1 ? ` +${input.allocations.length - 1}` : ""}`}
        recurringError={recurring.error}
        pendingConfirmation={savedOnDevice}
        onAddAnother={onAddAnother}
      />
    );
  return (
    <><ConfirmDialog state={confirm} onClose={() => setConfirm(null)} /><form className="expense-form" onSubmit={submit}>
      {locked && (
        <div className="pending-expense" role="status">
          <LockKeyhole size={17} />
          <span>
            <LoadingLabel>Saving</LoadingLabel>
          </span>
        </div>
      )}
      <fieldset disabled={locked || busy || externalBusy || freezeFields}>
        {loanTransaction ? <LoanTransactionFields input={input} field={field} account={account} payees={data.payees || []} /> : <>
          {!onReview || !initialInput || !freezeFields ? <label>Entry type<select aria-label="Entry type" value={entryType} disabled={!!initialInput && !!onReview} onChange={e => changeEntryType(e.target.value as "Expense" | "Income" | "Transfer")}><option>Expense</option><option>Income</option><option>Transfer</option></select></label> : null}
          <EntryAmountField currency={account?.currency || "MVR"} amount={input.amount} autoFocus={autoFocusAmount} onChange={changeAmount} />
          <EntryAccountField
            entryType={entryType}
            input={input}
            setInput={setInput}
            accounts={accounts}
            trips={trips}
            tripAccounts={tripAccounts}
            curTrip={curTrip}
            destination={destination}
            profileNames={profileNames}
          />
          {entryType === "Transfer" && (
            <Suspense fallback={<p className="settings-note">Loading transfer accounts…</p>}><TransferFields
              input={input}
              setInput={setInput}
              field={field}
              accounts={accounts}
              account={account}
              destination={destination}
              trip={curTrip}
              baseCurrency={data.baseCurrency}
              rates={data.rates}
            /></Suspense>
          )}
          <EntryTitlePayeeFields
            entryType={entryType}
            isIOU={isIOU}
            input={input}
            setInput={setInput}
            field={field}
            payees={data.payees || []}
            tripTitles={tripTitles}
          />
          <div className="expense-fields">
            {entryType !== "Transfer" && !isIOU && !input.allocations && (!receipt?.categorySplit || receipt.categorySplit.mode === "off") && (
              <EntryCategoryFields
                input={input}
                setInput={setInput}
                field={field}
                isIncome={isIncome}
                entryOptions={entryOptions}
                onOptionsSaved={setLocalEntryOptions}
                setCategoryError={setCategoryError}
              />
            )}
            <label>
              Date
              <DateInput
                value={input.date}
                onChange={(e) => field("date", e.target.value)}
                required
              />
            </label>
          </div>
          {entryType !== "Transfer" && !isIOU && !loanTransaction && allowConversion && !input.pending && (isCurrencyConversionCategory(input.category) || input.conversionTransactionId !== undefined) && <Suspense fallback={<p role="status">Loading exchange options…</p>}><EntryCurrencyConversionFields data={data} input={input} setInput={setInput} currency={account?.currency || "MVR"} sourceId={editingTransactionId} /></Suspense>}
          {entryType !== "Transfer" && !isIOU && <EntryAllocationFields receipt={receipt} changePlan={changePlan} input={receiptInput} setInput={setInput} entryOptions={entryOptions} onOptionsSaved={setLocalEntryOptions} setCategoryError={setCategoryError} currency={account?.currency || "MVR"} disabled={recurring.fields.makeRecurring} />}
          {trips.length > 0 && !isIOU && entryType !== "Transfer" && (
            <EntryTripFields
              input={input}
              setInput={setInput}
              trips={trips}
              accounts={accounts}
            />
          )}
        </>}
        <label className="expense-notes-field">
          Notes
          <textarea
            rows={2}
            value={input.notes}
            onChange={(e) => field("notes", e.target.value)}
            placeholder="Anything worth remembering"
            maxLength={1000}
          />
        </label>
        {categoryError && (
          <p className="form-error" role="alert">
            {categoryError}
          </p>
        )}
        {clearing && <p className="muted pending-hint">Update the amount and markup to what the bank charged, then save.</p>}
        <button
          type="button"
          className="text-button expense-more"
          aria-expanded={more}
          onClick={() => setMore(!more)}
        >
          <span className="expense-more-title">Optional details</span>
          <span className="expense-more-side" aria-hidden="true">
            {optionalCount > 0 && (
              <span className="expense-more-badge">{optionalCount} active</span>
            )}
            <ChevronDown
              size={16}
              className="expense-more-chevron"
              style={{ transform: more ? "rotate(180deg)" : undefined }}
            />
          </span>
        </button>
        {more && (
          <ExpenseOptionalDetails {...optionalProps} />
        )}
      </fieldset>
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      <div className="expense-save">
        <p>
          <ReceiptText size={15} /> Added by {profile} · {account?.owner}’s
          account
        </p>
        <button
          className="primary-button"
          disabled={busy || externalBusy || (!loanTransaction && !categories.length) || (isIOU && !input.payeeId)}
          type="submit"
        >
          {onReview
            ? reviewLabel
            : busy
              ? <LoadingLabel>Saving</LoadingLabel>
              : iouMode === "new"
                  ? "Save loan"
                  : iouMode === "settle"
                    ? "Save repayment"
                    : `Save ${entryType.toLowerCase()}`}
          <ArrowRight size={18} />
        </button>
      </div>
    </form></>
  );
}
